import IconAppsIntegrations from "@/icons/IconAppsIntegrations";
import IconAutomation from "@/icons/IconAutomation";
import IconActivity from "@/icons/IconActivity";
import IconBilling from "@/icons/IconBilling";
import IconChannels from "@/icons/IconChannels";
import IconGeneralSettings from "@/icons/IconGeneralSettings";
import IconHome from "@/icons/IconHome";
import IconWorkflows from "@/icons/IconWorkflows";
import { MenuItem } from "@/types";
import React, { useMemo } from "react";
import useSubscriptions from "./useSubscriptions";
import IconKnowledgeBase from "@/icons/IconKnowledgeBase";
import IconDesigner from "@/icons/IconDesigner";
import IconDeployment from "@/icons/IconDeployment";
import IconHelp from "@/icons/IconHelp";
import IconEmail from "@/icons/IconEmail";
import IconCalendarCheck from "@/icons/IconCalendarCheck";
import IconInsightsReports from "@/icons/IconInsightsReports";
import IconPersonTeam from "@/icons/IconPersonTeam";
import AuthService from "@/services/AuthService";

export default function useMenuItems() {
  const { subscription } = useSubscriptions();
  const isVelaroInternal = AuthService.profile.siteId === 1032;
  const isVelaroAdmin = AuthService.profile.isVelaroAdmin === true;
  const isVelaroBilling = AuthService.profile.isVelaroBilling === true;
  const isVelaroEmail = AuthService.profile.userName?.endsWith("@velaro.com") === true;
  // Staff = explicit IsVelaroSupport or IsVelaroAdmin DB flag — never inferred from siteId or email alone
  const isVelaroStaff = AuthService.profile.isVelaroSupport === true || isVelaroAdmin;
  // Billing access = full admin OR dedicated billing role (IsVelaroBilling)
  const hasBillingAccess = (isVelaroAdmin || isVelaroBilling) && isVelaroEmail;

  const menuItems: MenuItem[] = useMemo(() => {
    return [
      {
        label: "Dashboard",
        to: "/dashboard",
        icon: <IconHome />,
        enabled: true
      },
      {
        label: "Settings",
        // Group items never render as a direct link (MainLayout.tsx only reads `to`
        // when `sublinks` is absent) -- this exists purely so useBreadcrumb's
        // resolveParentHref has a target for the "Settings" parent crumb. Without it,
        // every /settings/*, /account, /teams, etc. page's parent crumb renders as
        // dead text and back-navigation falls through to raw browser history instead
        // of landing on /settings. Previously relied on a sublink literally labeled
        // "Settings Hub" (regex-matched on the word "hub") -- removed when Settings
        // Hub was merged into this page's own Quick Links section.
        to: "/settings",
        icon: <IconGeneralSettings />,
        sublinks: [
          {
            label: "Account",
            to: "/account",
            end: true,
            enabled: true
          },
          {
            label: "Users",
            to: "/users",
            enabled: true
          },
          {
            label: "Teams",
            to: "/teams",
            enabled: true
          },
          {
            label: "Routing & Escalation",
            to: "/routing/settings",
            enabled: true
          },
          {
            label: "Schedules",
            to: "/work_schedules",
            enabled: true
          },
          {
            label: "Skills",
            to: "/skills",
            enabled: true
          },
          {
            label: "Surveys",
            to: "/surveys",
            enabled: true
          },
          // Deployment promoted to its own top-level nav section below
          {
            label: "Invitations",
            to: "/invitations",
            enabled: subscription?.enableInvitations
          },
          {
            label: "Tags",
            to: "/tags",
            enabled: true
          },
          {
            label: "Custom Fields",
            to: "/custom-fields",
            enabled: true
          },
          {
            label: "Canned Responses",
            to: "/canned_responses",
            enabled: true
          },
          {
            label: "File Storage",
            to: "/files",
            enabled: true
          },
          {
            label: "Transcript Settings",
            to: "/account/transcript-settings",
            enabled: true
          },
          {
            label: "Usage",
            to: "/account/usage",
            enabled: true
          },
          {
            // Merges the former standalone "Settings Hub" directory in as a
            // "Quick Links" section at the top of this page's own left nav --
            // routes still registered for backward-compatible bookmarks.
            // Label distinct from the parent group's "Settings" label so the
            // breadcrumb doesn't render as a duplicated "Settings > Settings".
            label: "Workspace Settings",
            to: "/settings",
            end: true,
            enabled: true
          },
          {
            label: "Roles & Permissions",
            to: "/settings/roles",
            enabled: true
          },
          {
            label: "AI Provider",
            to: "/settings/ai-provider",
            enabled: true
          },
          {
            label: "Conversions",
            to: "/settings/conversions",
            enabled: true
          },
          {
            label: "Condition Presets",
            to: "/settings/condition-presets",
            enabled: true
          },
          {
            label: "Compliance",
            to: "/settings/compliance",
            enabled: subscription?.enableComplianceMode
          }
        ],
        enabled: true
      },
      {
        label: "Designer",
        icon: <IconDesigner />,
        enabled: true,
        sublinks: [
          {
            label: "Chat Window",
            to: "/chat_window",
            enabled: true,
            end: false
          }
        ]
      },
      {
        label: "Deployment",
        icon: <IconDeployment />,
        enabled: true,
        sublinks: [
          {
            label: "Chat Embedding",
            to: "/deployment",
            enabled: true
          },
          {
            // Fully built and live (Bundle/list, Bundle/default, etc. all real
            // endpoints on velaro-messaging's BundleController) but had no nav
            // entry anywhere -- unreachable except by typing the URL directly.
            // Label matches the page's own RouteHeader title exactly.
            label: "Chat Experience",
            to: "/chat-experience",
            enabled: true
          },
          {
            label: "Preview Chat",
            to: "/chat-preview",
            enabled: true
          },
          {
            label: "Page Widgets",
            to: "/page-widgets",
            enabled: true
          },
          {
            label: "Proactive Invitations",
            to: "/proactive-engagement",
            enabled: true
          }
        ]
      },
      {
        label: "Workflows",
        icon: <IconWorkflows />,
        enabled: true,
        sublinks: [
          {
            label: "My Workflows",
            to: "/workflows",
            enabled: true
          },
          {
            label: "Variable Guide",
            to: "/workflows/variable-guide",
            enabled: true
          }
        ]
      },
      {
        label: "Visitor Monitor",
        to: "/visitor-monitor",
        icon: <IconPersonTeam />,
        enabled: subscription?.enableVisitorTracking
      },
      {
        label: "Outreach",
        icon: <IconEmail />,
        enabled: !!(subscription?.enableEmailCampaigns || subscription?.enableIvr),
        sublinks: [
          {
            label: "Email Campaigns",
            to: "/email-campaigns",
            enabled: subscription?.enableEmailCampaigns
          },
          {
            label: "IVR Campaigns",
            to: "/ivr-campaigns",
            enabled: subscription?.enableIvr
          },
          {
            label: "Segments",
            to: "/contact-segments",
            enabled: subscription?.enableEmailCampaigns
          }
        ]
      },
      {
        label: "Appointments",
        to: "/appointments",
        icon: <IconCalendarCheck />,
        enabled: subscription?.enableAppointments
      },
      // {
      //   label: "Bots and Automation",
      //   to: "/bots",
      //   icon: <IconAutomation />,
      //   enabled: subscription?.enableAutomation
      // },
      // JEROME: The old "AI Chatbot" menu has been hidden (enabled: false) on 2026-03-08.
      // The code is preserved below — do NOT remove it. To restore, change enabled back to true
      // (or subscription?.enableAI). The new "AI Chatbot" menu (formerly "AI Chatbot New") is
      // now the only visible AI chatbot entry and includes Chatbot Profile.
      {
        label: "AI Chatbot (Legacy — Hidden)",
        icon: <IconAutomation />,
        enabled: false, // JEROME: intentionally disabled — was: true / subscription?.enableAI
        sublinks: [
          {
            label: "Chatbot Settings",
            to: "/ai",
            enabled: true
          },
          {
            label: "Chatbot Profile",
            to: "/bots",
            enabled: true
          },
          {
            label: "Chatbot Training",
            to: "/bots/training/index",
            enabled: true
          }
        ]
      },
      {
        label: "AI Chatbot",
        icon: <IconAutomation />,
        enabled: true,
        sublinks: [
          {
            label: "Bot Setup Wizard",
            to: "/chatbots/new",
            enabled: true
          },
          {
            label: "Bot Personas",
            to: "/bots",
            enabled: true
          },
          {
            label: "AI Configurations",
            to: "/ai-new/settings",
            enabled: true
          },
          {
            label: "Chatbot Training",
            to: "/ai-new/training",
            enabled: true
          },
          {
            label: "AI Indexes",
            to: "/ai-new/indexes",
            enabled: true
          }
        ]
      },
      // Rules panel visibility is a pure UI toggle. Saved Rules continue to fire at
      // runtime regardless of this flag. Default: existing customers see the panel
      // (Startup guard sets ShowRulesPanel=1 for existing rows), new signups do not.
      // The /rules route remains registered in Routes.tsx for direct URL access.
      ...(subscription?.showRulesPanel
        ? [
            {
              label: "Rules",
              icon: <IconWorkflows />,
              enabled: true,
              sublinks: [
                {
                  label: "Rules",
                  to: "/rules",
                  enabled: true
                },
                {
                  label: "Routing History",
                  to: "/routing-history",
                  enabled: true
                },
                {
                  label: "Routing Activity",
                  to: "/routing/activity",
                  enabled: true
                }
              ]
            }
          ]
        : []),
      {
        label: "Focus Inbox",
        to: "/focus-inbox",
        icon: (
          <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round">
            <path d="M13 2 3 14h9l-1 8 10-12h-9l1-8Z" />
          </svg>
        ),
        // Show if enabled; always show as a menu item so upsell is visible to all users
        enabled: true
      },
      {
        // Adds a Live/History toggle to What's Running — History is a quick
        // conversation-ID lookup (the Decision Log, previously only reachable via
        // a button on Routing & Escalation). The full filterable/groupable
        // Routing History page (with retrigger simulation) is still its own item
        // above — not a duplicate, it's a richer tool for a different job.
        label: "Activity",
        to: "/whats-running",
        icon: <IconActivity />,
        enabled: true
      },
      {
        label: "Billing",
        icon: <IconBilling />,
        enabled: true,
        sublinks: [
          // Customer-facing — always visible to all users.
          {
            label: "Usage & Plan",
            to: "/billing",
            end: true,
            enabled: true
          },
          // Operations — Velaro billing staff only (IsVelaroAdmin OR IsVelaroBilling + @velaro.com).
          {
            label: "Billing Guide",
            to: "/admin/billing-guide",
            enabled: hasBillingAccess
          },
          {
            label: "Financial Dashboard",
            to: "/admin/billing-financial",
            enabled: hasBillingAccess
          },
          {
            label: "Per-Site Billing",
            to: "/admin/billing-staff",
            enabled: hasBillingAccess
          },
          {
            label: "Plans & Pricing",
            to: "/admin/plans-pricing",
            enabled: hasBillingAccess
          },
          {
            label: "Overage & Auto-Upgrade",
            to: "/admin/billing-overage",
            enabled: hasBillingAccess
          },
          {
            label: "Subscription Settings",
            to: "/admin/subscription-settings",
            enabled: hasBillingAccess
          },
          {
            label: "New Customer Setup",
            to: "/admin/subscription-new",
            enabled: hasBillingAccess
          },
          {
            label: "Quote Builder",
            to: "/admin/pricing-quote",
            enabled: hasBillingAccess
          },
          // Documents & Config
          {
            label: "Invoices",
            to: "/billing/invoices",
            enabled: hasBillingAccess
          },
          {
            label: "Contracts",
            to: "/billing/contracts",
            enabled: hasBillingAccess
          },
          {
            label: "Templates",
            to: "/billing/templates",
            enabled: hasBillingAccess
          },
          {
            label: "Usage Pack Purchases",
            to: "/billing/usage-packs",
            enabled: hasBillingAccess
          },
          {
            label: "Email Campaign Usage",
            to: "/billing/campaign-usage",
            enabled: hasBillingAccess
          },
          {
            label: "Stripe Settings",
            to: "/billing/settings",
            enabled: hasBillingAccess
          },
          {
            label: "QuickBooks Mapping",
            to: "/billing/qb-mapping",
            enabled: hasBillingAccess
          },
          {
            label: "StaxBill Import",
            to: "/admin/billing-financial?tab=staxbill",
            enabled: hasBillingAccess
          },
        ]
      },
      {
        label: "Channels",
        icon: <IconChannels />,
        sublinks: [
          {
            label: "Facebook",
            to: "/facebook",
            enabled: subscription?.enableFacebook
          },
          {
            label: "Instagram",
            to: "/instagram",
            enabled: subscription?.enableInstagram
          },
          {
            label: "TikTok",
            to: "/tiktok",
            enabled: subscription?.enableTikTok
          },
          {
            label: "SMS",
            to: "/sms",
            enabled: subscription?.enableSms
          },
          {
            label: "WhatsApp",
            to: "/whatsapp",
            enabled: subscription?.enableWhatsapp
          },
          {
            label: "RCS",
            to: "/rcs",
            enabled: subscription?.enableRcs
          },
          {
            label: "Amazon Alexa",
            to: "/alexa",
            enabled: subscription?.enableAlexaVoice
          },
          {
            label: "IVR",
            to: "/ivr",
            enabled: subscription?.enableIvr
          },
          {
            label: "Click-to-Call",
            to: "/click-to-call",
            enabled: subscription?.enableClickToCallWidget
          },
          {
            label: "AI Video Avatars",
            to: "/avatar",
            enabled: subscription?.enableAvatarMode
          }
        ],
        enabled:
          subscription?.enableFacebook ||
          subscription?.enableInstagram ||
          subscription?.enableTikTok ||
          subscription?.enableSms ||
          subscription?.enableWhatsapp ||
          subscription?.enableRcs ||
          subscription?.enableAlexaVoice ||
          subscription?.enableIvr ||
          subscription?.enableClickToCallWidget ||
          subscription?.enableAvatarMode
      },
      {
        label: "Knowledge Base",
        to: "/kb",
        icon: <IconKnowledgeBase />,
        sublinks: [
          {
            label: "Settings",
            to: "/kb/settings",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Topics",
            to: "/kb/topics",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Articles",
            to: "/kb/articles",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Site Map",
            to: "/kb/map",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Performance",
            to: "/kb/performance",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Search Widget",
            to: "/kb/widget",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Asset Library",
            to: "/kb/assets",
            enabled: subscription?.enableKnowledgeBase
          },
          {
            label: "Import",
            to: "/kb/migrate",
            enabled: subscription?.enableKbImport ?? subscription?.enableKnowledgeBase
          }
        ],
        enabled: subscription?.enableKnowledgeBase
      },
      {
        label: "Reports",
        icon: <IconInsightsReports />,
        enabled: true,
        sublinks: [
          { label: "Value Report", to: "/reports/value", enabled: true },
          { label: "Conversions", to: "/reports/conversions", enabled: true },
          { label: "Agent Performance", to: "/reports/agent-performance", enabled: true },
          { label: "Leaderboard", to: "/reports/leaderboard", enabled: subscription?.enableAgentLeaderboard ?? false },
          { label: "Chat Intelligence", to: "/reports/chat-intelligence", enabled: true },
          { label: "IVR Outbound", to: "/reports/ivr-campaigns", enabled: subscription?.enableOutboundIvr ?? false },
          { label: "Call Center Analytics", to: "/reports/call-center", enabled: true },
          { label: "Agent Connectivity", to: "/reports/agent-connectivity", enabled: true },
          { label: "Call Attribution", to: "/reports/call-attribution", enabled: true },
          { label: "Call Activity", to: "/reports/call-activity", enabled: true }
        ]
      },
      {
        // The ~54 individual integrations below used to be listed here one-by-one as
        // separate sidebar sublinks. /integrations (IntegrationsLanding.tsx) is already
        // a fully-built searchable, filterable, categorized directory covering all of
        // them -- the flat sidebar list was pure redundant clutter on top of it, not
        // extra functionality (every route below still exists and works if bookmarked
        // or deep-linked; only these unused shortcut entries are removed). Confirmed
        // with Alex 2026-08-01: no real customers using this nav yet, safe to simplify.
        label: "Integrations",
        to: "/integrations",
        icon: <IconAppsIntegrations />,
        enabled: true,
        // No sublinks anymore to highlight individual integration sub-pages, and this
        // item's page owns the whole /integrations/* subtree -- prefix-match so the
        // sidebar still shows "you are here" on /integrations/hubspot etc.
        end: false
      },
      // Velaro-internal tools — only visible to site 1032
      {
        label: "Velaro Admin",
        icon: <IconGeneralSettings />,
        enabled: isVelaroAdmin && isVelaroEmail,
        sublinks: [
          {
            label: "Velaro Logger",
            to: "/admin/velaro-logger",
            enabled: true
          },
          {
            label: "Superadmin",
            to: "/admin/database-tool",
            enabled: true
          },
          {
            label: "KB Custom Domains",
            to: "/admin/kb-domains",
            enabled: true
          },
          {
            label: "Site Sync",
            to: "/admin/site-sync",
            enabled: true
          },
          {
            label: "App Marketplace",
            to: "/admin/app-marketplace",
            enabled: true
          },
          {
            label: "System Health",
            to: "/admin/system-health",
            enabled: true
          },
          {
            label: "Live Monitor",
            to: "/admin/live-monitor",
            enabled: true
          },
          {
            label: "Hangfire Monitor",
            to: "/admin/hangfire",
            enabled: true
          },
          {
            label: "Account Health",
            to: "/admin/account-health",
            enabled: true
          },
          {
            label: "AM Assignments",
            to: "/admin/am-assignments",
            enabled: true
          },
          {
            label: "AI Platform",
            to: "/admin/copilot",
            enabled: true
          },
          {
            label: "Activity Log",
            to: "/admin/activity-log",
            enabled: true
          },
          {
            label: "Deploy History",
            to: "/admin/deploy-history",
            enabled: true
          },
          {
            label: "Workflow Logs",
            to: "/admin/workflow-logs",
            enabled: true
          },
          {
            label: "Background Job Migration",
            to: "/admin/background-jobs",
            enabled: true
          },
          {
            label: "Skill Traces",
            to: "/admin/skill-traces",
            enabled: true
          },
          {
            label: "Skill Routing Overrides",
            to: "/admin/skill-routing-overrides",
            enabled: true
          },
          {
            label: "Platform Setup",
            to: "/admin/platform-setup",
            enabled: true
          },
          {
            label: "Design System (2026)",
            to: "/admin/design-v2",
            enabled: true
          },
          {
            label: "AI Prompt Policies",
            to: "/admin/ai-prompt-policy",
            enabled: true
          },
          /* DEAD — removed from nav, available in marketing repo. Kept for 30s revival.
          {
            label: "MF Athletic Demo",
            to: "/admin/mf-athletic-demo",
            enabled: true
          }, */
          {
            label: "Support Tools",
            to: "/admin/support-tools",
            enabled: true
          },
          {
            label: "Connection Architecture",
            to: "/admin/internal-docs/v10-vs-v20-connection-model",
            enabled: true
          }
        ]
      },
      // Velaro Support — visible to any @velaro.com staff with isVelaroSupport or isVelaroAdmin flag
      // who are NOT full admins (to avoid duplicating items already in the Velaro Admin section above).
      {
        label: "Velaro Support",
        icon: <IconGeneralSettings />,
        enabled: isVelaroStaff && !isVelaroAdmin && isVelaroEmail,
        sublinks: [
          {
            label: "Velaro Logger",
            to: "/admin/velaro-logger",
            enabled: true
          },
          {
            label: "Deployments",
            to: "/admin/deployments",
            enabled: true
          },
          {
            label: "Deploy History",
            to: "/admin/deploy-history",
            enabled: true
          },
          {
            label: "Workflow Logs",
            to: "/admin/workflow-logs",
            enabled: true
          },
          {
            label: "Background Job Migration",
            to: "/admin/background-jobs",
            enabled: true
          },
          {
            label: "Skill Traces",
            to: "/admin/skill-traces",
            enabled: true
          },
          {
            label: "Skill Routing Overrides",
            to: "/admin/skill-routing-overrides",
            enabled: true
          },
          {
            label: "Connection Architecture",
            to: "/admin/internal-docs/v10-vs-v20-connection-model",
            enabled: true
          }
        ]
      },
      // Help & Support is intentionally last — always pinned to the bottom of the nav
      {
        label: "Help & Support",
        icon: <IconHelp />,
        enabled: true,
        sublinks: [
          {
            label: "Help Center",
            to: "https://help.velaro.com/",
            enabled: true
          },
          {
            label: "Contact Support",
            to: "/support",
            enabled: true
          }
        ]
      }
    ];
  }, [subscription]);

  return menuItems;
}
